'use client'

import { useEffect, useMemo, useRef, type ComponentProps } from 'react'

import { usePathname } from 'next/navigation'

import styles from '@/app/style-guide/hearing-aids-hub/hub.module.css'
import { Anchor } from '@/components/client/seo/anchor'
import { HubVisitContext } from '@/hooks/client/hub-visit-context'
import { useTranslation } from '@/hooks/client/use-translation'
import { HubVisit } from '@/lib/analytics/hub-visit'

import { useHubAnalytics } from './use-hub-analytics'

// The shared header can wrap with enlarged text. Keep overlays and anchor
// clearance tied to its in-flow height, including when the bar scrolls away.
export const HubShell = ({ children, className = '', ...props }: ComponentProps<'div'>) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const pathname = usePathname()
  const visit = useMemo(() => new HubVisit(pathname), [pathname])
  useHubAnalytics(ref, visit)
  useEffect(() => {
    const shell = ref.current
    const topbar = shell?.querySelector('.topbar-wrapper')?.parentElement
    if (!shell || !topbar) {
      return
    }
    const update = () => {
      shell.style.setProperty('--hub-nav-height', `${topbar.getBoundingClientRect().height}px`)
    }
    const observer = new ResizeObserver(update)
    observer.observe(topbar)
    update()
    return () => observer.disconnect()
  }, [])
  return (
    <div {...props} ref={ref} className={`ht-wrapper bg-cream ${styles.shell} ${className}`} data-hub-shell>
      <Anchor data-tracking="ignore" className={styles.skip} href="#article">
        {t('hub.skip', 'Skip to hearing aid content')}
      </Anchor>
      <HubVisitContext value={visit}>{children}</HubVisitContext>
    </div>
  )
}
